/**
 * The pure sizing arithmetic behind `Image`: what widths the srcset offers, what `sizes` tells the
 * browser, and what dimensions actually reach the derivative URL.
 *
 * Its own module because it is where this component touches the CDN's cache key, and that deserves a
 * test that does not need React, a router context or a DOM to run.
 */

/**
 * The grid every REQUESTED dimension is snapped to, and the reason this file has to care about the
 * CDN at all: the derivative URL is CloudFront's cache key, so two requests for the same picture a
 * pixel apart are two objects — fetched from imgproxy separately at every POP, and each competing for
 * the same LRU space that decides how long either survives.
 *
 * That is measured, not theoretical. One live portal page asked for `w:381` AND `w:380` of a single
 * logo, and `h:222/w:320` alongside `h:220/w:320` of a single photo. Nothing varied but the caller's
 * `aspectRatio`, which differs in the third decimal between two components drawing the same image and
 * lands `Math.round` on adjacent pixels.
 *
 * 8 rather than 4 because it collapses roughly four times as many neighbours, and the worst case is a
 * 4px deviation — under half a percent at the sizes served here, on a bitmap the browser then scales
 * into a CSS box it sized itself. Nearest rather than down, so a request never asks for FEWER pixels
 * than a high-density screen wants; asking for a few more is free, since imgproxy clamps to the source
 * and never enlarges.
 *
 * This snaps the REQUEST only. The `width`/`height` attributes `Image` renders keep their exact
 * values, because those are the CLS reservation and the layout — moving them to an 8px grid would
 * shift the page to save bytes somewhere the reader cannot see.
 */
export const DIMENSION_STEP = 8;

export function snapDimension(value: number | undefined): number | undefined {
	if (value === undefined || value <= 0) return value;
	return Math.max(DIMENSION_STEP, Math.round(value / DIMENSION_STEP) * DIMENSION_STEP);
}

/**
 * Anchor breakpoints on the actual display width: 1×, 1.5× and 2× cover all device pixel ratios in
 * use today with ≥25% steps between variants. 160 and 320 are kept as mobile-fallback floors. This
 * trims the srcset from 10–11 entries down to 4–5 per image, halving the bytes and the CDN cache
 * surface.
 *
 * The BASE is snapped before the multiples are derived from it, not merely the results: snapping only
 * at the end still splits the 1.5× step, because 380 and 381 round to 570 and 572 first and those sit
 * either side of a grid line (568 against 576). Snapping the base makes two callers a pixel apart
 * agree on the WHOLE srcset rather than on three quarters of it. Deduped after, since two steps can
 * now land on one width, and the same URL twice under different `w` descriptors says nothing twice.
 */
export function getBreakpoints(width: number): number[] {
	const base = snapDimension(width) ?? width;
	const candidates = [160, 320, base, snapDimension(Math.round(base * 1.5)) ?? base, base * 2].filter(
		(w) => w <= base * 2,
	);
	return [...new Set(candidates)].sort((a, b) => a - b);
}

/**
 * What the browser is told the image will be drawn at, so it does not assume 100vw and fetch the
 * widest candidate on every screen.
 *
 * The only width this module can state as a fact is the image's OWN: below it the picture may fill
 * the viewport, above it it never grows past itself. Anything more precise is the caller's layout,
 * which is not visible from here.
 *
 * It used to descend through `getBreakpoints`, and that is what made a large photo render blurred.
 * Two faults, either of which is enough on its own. The steps were filtered with `bp <= width` while
 * the breakpoints had already been snapped to the 8px grid, so a width that rounds UP lost its own
 * step: 1798 snaps to 1800, 1800 > 1798, and the attribute came out as `(min-width: 320px) 320px,
 * 160px` — 320 CSS pixels claimed for an image drawn across nine hundred. And a step that survived
 * was declared as `(min-width: <its own width>)`, a condition a 1440px screen does not meet for any
 * image wider than it, so those fell through to the 320 floor too. At DPR 2 the browser needed 640
 * and climbed to the next candidate anyway, which is why this only showed on ordinary screens.
 */
export function getSizes(width: number): string {
	const base = snapDimension(width) ?? width;
	return `(max-width: ${base}px) 100vw, ${base}px`;
}
